import { useMemo } from 'react'
import { useTrainingPeriodStore } from 'pages/site/models'
import { Text } from 'src/components/ui'
import useTimeRange from 'src/contexts/timeRange'
import { timeRangeOrPeriodToTimeRange } from 'src/utility/time'

function formatNumber(n: number | undefined): string {
  return n === undefined ? '' : n.toFixed(2)
}

function valueWithUnit(
  value: number | undefined,
  unit: string | null | undefined,
): string {
  if (unit) {
    return `${formatNumber(value)} ${unit}`
  }
  return `${formatNumber(value)}`
}

type Props = {
  actualValue: number | undefined
  targetValue: number
  unit: string | null | undefined
}

export function PrescriptiveLegend({
  actualValue,
  targetValue,
  unit,
}: Props): JSX.Element {
  const { timeRange } = useTimeRange()
  const { excludedPeriods } = useTrainingPeriodStore()

  const includesExcludedPeriod = useMemo(() => {
    if (excludedPeriods.length === 0) return false
    return excludedPeriods.some(p => {
      if (!p.timeRange) return false
      const periodTimeRange = timeRangeOrPeriodToTimeRange(p.timeRange)
      return (
        periodTimeRange.from <= timeRange.to &&
        timeRange.from <= periodTimeRange.to
      )
    })
  }, [excludedPeriods, timeRange])
  return (
    <div className="flex gap-s">
      {includesExcludedPeriod && (
        <div className="flex gap-2xs">
          <div className="size-s rounded-[2px] bg-black/20" />
          <Text className="text-text-secondary">Excluded period</Text>
        </div>
      )}

      <div className="flex flex-col items-end">
        <div className="flex items-center gap-2xs">
          <div className="size-s rounded-[2px] bg-[#726ADF]" />
          <Text className="text-text-secondary">Actual value</Text>
        </div>
        <Text bold>{valueWithUnit(actualValue, unit)}</Text>
      </div>
      <div className="flex flex-col items-end">
        <div className="flex items-center gap-2xs">
          <div className="size-s rounded-[2px] bg-text-warning" />
          <Text className="text-text-secondary">Target value</Text>
        </div>
        <Text bold>{valueWithUnit(targetValue, unit)}</Text>
      </div>
    </div>
  )
}
